import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Button,
  Stack,
  Collapse,
  Divider,
  Alert,
  Link,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Public as PublicIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { exposureService } from '../services/exposureService';
import { authService } from '../services/authService';
import { formatCommentExportTime } from '../utils/formatFacebookActivityLine';
import FacebookActivityLine from './FacebookActivityLine';
import { tokens } from '../theme/tokens';

const TEXT_PRIMARY = tokens.text.primary;
const TEXT_MUTED = tokens.text.muted;
const CARD_WHITE = tokens.bg.card;
const RISK_BORDER_HIGH = tokens.accent.danger;
const RISK_BORDER_MEDIUM = tokens.accent.warning;
const RISK_BORDER_LOW = tokens.accent.safe;

/**
 * ExposureCard Component
 * Displays a single privacy exposure with details and recommendations
 * @param {object} exposure - Exposure data
 * @param {function} onUpdate - Callback when exposure is updated
 */
const ExposureCard = ({ exposure, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);

  /** Absolute URL for opening the post/comment (Reddit may store a path only). */
  const resolvedExposureUrl = useMemo(() => {
    const u = exposure.url;
    if (!u) return '';
    if (u.startsWith('http')) return u;
    if (exposure.platform === 'reddit') {
      return `https://reddit.com${u.startsWith('/') ? u : `/${u}`}`;
    }
    return '';
  }, [exposure.url, exposure.platform]);

  const getRiskBorderHex = (level) => {
    switch (level?.toLowerCase()) {
      case 'high':
        return RISK_BORDER_HIGH;
      case 'medium':
        return RISK_BORDER_MEDIUM;
      case 'low':
        return RISK_BORDER_LOW;
      default:
        return 'rgba(15, 23, 42, 0.22)';
    }
  };

  const riskLevelKey = (exposure.risk_level || '').toLowerCase();
  const severityPercent =
    riskLevelKey === 'high' ? 100 : riskLevelKey === 'medium' ? 66 : riskLevelKey === 'low' ? 33 : 0;
  const riskBarColor = getRiskBorderHex(exposure.risk_level);

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'email':
        return <EmailIcon />;
      case 'phone':
        return <PhoneIcon />;
      case 'address':
      case 'address_hint':
      case 'location':
        return <LocationIcon />;
      case 'reddit_username':
      case 'reddit_profile_bio':
        return <PublicIcon />;
      default:
        return <WarningIcon />;
    }
  };

  const getTypeLabel = (type) => {
    if (!type) return 'Unknown';
    const t = String(type).toLowerCase();
    if (t === 'reddit_username') return 'Reddit username';
    if (t === 'reddit_profile_bio') return 'Profile bio';
    if (t === 'address_hint' || t === 'location') return 'Address';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getRiskLabel = (level) => {
    return level ? level.charAt(0).toUpperCase() + level.slice(1) : 'Unknown';
  };

  const riskChipSx =
    riskLevelKey === 'high'
      ? {
          fontWeight: 700,
          bgcolor: 'rgba(239, 68, 68, 0.16)',
          color: '#B91C1C',
          border: '1px solid rgba(239, 68, 68, 0.45)',
          boxShadow: '0 0 14px rgba(239, 68, 68, 0.2)',
        }
      : riskLevelKey === 'medium'
        ? {
            fontWeight: 700,
            bgcolor: 'rgba(245, 158, 11, 0.18)',
            color: '#B45309',
            border: '1px solid rgba(245, 158, 11, 0.45)',
            boxShadow: '0 0 14px rgba(245, 158, 11, 0.18)',
          }
        : riskLevelKey === 'low'
          ? {
              fontWeight: 700,
              bgcolor: 'rgba(34, 197, 94, 0.16)',
              color: '#15803D',
              border: '1px solid rgba(34, 197, 94, 0.45)',
              boxShadow: '0 0 14px rgba(34, 197, 94, 0.18)',
            }
          : {
              fontWeight: 700,
              bgcolor: 'rgba(15, 23, 42, 0.06)',
              color: TEXT_PRIMARY,
            };

  const showYoutubeActivity =
    exposure.platform === 'youtube' &&
    exposure.source === 'comment' &&
    (exposure.video_title || exposure.channel_title || exposure.url);
  const showFacebookActivity =
    exposure.platform === 'facebook' &&
    exposure.source === 'comment' &&
    (exposure.context_title || exposure.comment_author);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid rgba(148, 163, 184, 0.24)',
        borderLeftWidth: 4,
        borderLeftStyle: 'solid',
        borderLeftColor: getRiskBorderHex(exposure.risk_level),
        boxShadow: '0 10px 26px rgba(2, 6, 23, 0.45)',
        bgcolor: CARD_WHITE,
        transition: 'box-shadow 0.25s ease, transform 0.25s ease',
        '&:hover': {
          boxShadow: '0 14px 34px rgba(2, 6, 23, 0.55)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ py: 2.75, px: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ color: getRiskBorderHex(exposure.risk_level) }}>{getTypeIcon(exposure.exposure_type)}</Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: TEXT_PRIMARY, letterSpacing: '-0.02em' }}>
                {getTypeLabel(exposure.exposure_type)} Exposure
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_MUTED, fontWeight: 500, mt: 0.25 }}>
                {exposure.platform?.charAt(0).toUpperCase() + exposure.platform?.slice(1) || 'Unknown Platform'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip label={getRiskLabel(exposure.risk_level)} size="small" sx={riskChipSx} />
            {exposure.is_resolved && (
              <Chip
                label="Resolved"
                color="success"
                size="small"
                icon={<CheckCircleIcon />}
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>
        </Box>

        <LinearProgress
          variant="determinate"
          value={severityPercent}
          sx={{
            height: 6,
            borderRadius: 3,
            mb: 2,
            bgcolor: 'rgba(148, 163, 184, 0.2)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              bgcolor: riskBarColor,
              transition: 'transform 0.35s ease',
            },
          }}
        />

        {(showYoutubeActivity || showFacebookActivity) && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
            <PublicIcon sx={{ fontSize: 20, color: TEXT_MUTED }} />
            <Typography
              variant="overline"
              sx={{ fontWeight: 800, letterSpacing: '0.1em', color: TEXT_PRIMARY, lineHeight: 1.2 }}
            >
              Activity
            </Typography>
          </Stack>
        )}

        {/* YouTube: which video / channel this comment belongs to */}
        {showYoutubeActivity && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: 'rgba(22, 35, 58, 0.95)',
              borderRadius: 2,
              border: '1px solid rgba(148, 163, 184, 0.3)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TEXT_PRIMARY }} gutterBottom>
              Where this comment was posted
            </Typography>
            {exposure.video_title ? (
              <Typography variant="body1" sx={{ mb: 0.5, color: TEXT_PRIMARY, fontSize: '1.0625rem' }}>
                <strong>Video:</strong>{' '}
                {exposure.url?.startsWith('http') ? (
                  <Link href={exposure.url} target="_blank" rel="noopener noreferrer" underline="hover">
                    {exposure.video_title}
                  </Link>
                ) : (
                  exposure.video_title
                )}
              </Typography>
            ) : exposure.url?.startsWith('http') ? (
              <Typography variant="body1" sx={{ mb: 0.5, color: TEXT_PRIMARY, fontSize: '1.0625rem' }}>
                <strong>Video:</strong>{' '}
                <Link href={exposure.url} target="_blank" rel="noopener noreferrer" underline="hover">
                  Open video
                </Link>
              </Typography>
            ) : null}
            {exposure.channel_title ? (
              <Typography variant="body1" sx={{ color: TEXT_PRIMARY, fontSize: '1.0625rem' }}>
                <strong>Channel:</strong>{' '}
                {exposure.channel_url?.startsWith('http') ? (
                  <Link href={exposure.channel_url} target="_blank" rel="noopener noreferrer" underline="hover">
                    {exposure.channel_title}
                  </Link>
                ) : (
                  exposure.channel_title
                )}
              </Typography>
            ) : null}
            {exposure.created_at && (
              <Typography variant="body1" sx={{ mt: 1, fontWeight: 700, color: TEXT_PRIMARY, fontSize: '1.0625rem' }}>
                Comment time: {formatCommentExportTime(exposure.created_at)}
              </Typography>
            )}
            {!exposure.video_title && !exposure.channel_title && exposure.url?.startsWith('http') && (
              <Typography variant="body2" display="block" sx={{ mt: 0.5, color: TEXT_MUTED }}>
                Video title was unavailable (private/deleted video or API limit). Use the video link to find your comment.
              </Typography>
            )}
          </Box>
        )}

        {/* Facebook: DYI context for comments */}
        {showFacebookActivity && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: 'rgba(22, 35, 58, 0.95)',
              borderRadius: 2,
              border: '1px solid rgba(148, 163, 184, 0.3)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TEXT_PRIMARY }} gutterBottom>
              Where this comment was posted (from your Facebook export)
            </Typography>
            {exposure.context_title ? (
              resolvedExposureUrl || exposure.facebook_activity_fallback_url ? (
                <FacebookActivityLine
                  text={exposure.context_title}
                  href={resolvedExposureUrl || exposure.facebook_activity_fallback_url}
                />
              ) : (
                <FacebookActivityLine text={exposure.context_title} />
              )
            ) : null}
            {exposure.created_at && (
              <Typography variant="body1" sx={{ mt: 1, fontWeight: 700, color: TEXT_PRIMARY, fontSize: '1.0625rem' }}>
                Comment time (from your export): {formatCommentExportTime(exposure.created_at)}
              </Typography>
            )}
            {exposure.comment_author ? (
              <Typography variant="body1" sx={{ color: TEXT_PRIMARY, mt: exposure.context_title || exposure.created_at ? 1 : 0, fontSize: '1.0625rem' }}>
                <strong>Author name in export:</strong> {exposure.comment_author}
              </Typography>
            ) : null}
          </Box>
        )}

        {/* Exposed Data — link to post/comment when backend provides a URL */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <WarningIcon sx={{ fontSize: 20, color: RISK_BORDER_HIGH }} />
          <Typography
            variant="overline"
            sx={{ fontWeight: 800, letterSpacing: '0.1em', color: TEXT_PRIMARY, lineHeight: 1.2 }}
          >
            Exposed data
          </Typography>
          {(resolvedExposureUrl ||
            (Array.isArray(exposure.exposed_data_items) &&
              exposure.exposed_data_items.some((it) => it.url && String(it.url).startsWith('http')))) && (
            <Typography component="span" variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 400 }}>
              (tap a value to open on {exposure.platform === 'facebook' ? 'Facebook' : exposure.platform})
            </Typography>
          )}
        </Stack>
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: 'rgba(239, 68, 68, 0.14)',
            borderRadius: 2,
            border: '1px solid rgba(239, 68, 68, 0.15)',
          }}
        >
          {/* Multi-item list when backend grouped several matches from the same comment */}
          {Array.isArray(exposure.exposed_data_items) && exposure.exposed_data_items.length > 0 ? (
            <Stack spacing={1}>
              {exposure.exposed_data_items.map((item, idx) => {
                const itemHref =
                  (item.url && String(item.url).startsWith('http') ? item.url : '') || resolvedExposureUrl;
                return (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Chip
                    label={item.type || 'match'}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      height: 20,
                      bgcolor:
                        item.risk_level === 'high' ? 'rgba(239,68,68,0.22)' :
                        item.risk_level === 'medium' ? 'rgba(245,158,11,0.22)' :
                        'rgba(34,197,94,0.22)',
                      color:
                        item.risk_level === 'high' ? '#F87171' :
                        item.risk_level === 'medium' ? '#FBBF24' :
                        '#4ADE80',
                      border: 'none',
                    }}
                  />
                  {itemHref ? (
                    <Link
                      href={itemHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        color: RISK_BORDER_HIGH,
                        fontWeight: 600,
                        wordBreak: 'break-word',
                        fontSize: '0.95rem',
                      }}
                    >
                      {item.value}
                    </Link>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        color: RISK_BORDER_HIGH,
                        fontWeight: 600,
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.value}
                    </Typography>
                  )}
                </Box>
              );
              })}
            </Stack>
          ) : (
            /* Legacy single-item display */
            <>
              <Typography variant="body2" sx={{ color: TEXT_MUTED, fontWeight: 600, mb: 0.5 }}>
                <strong>Exposed Data:</strong>
              </Typography>
              {resolvedExposureUrl ? (
                <Link
                  href={resolvedExposureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    color: RISK_BORDER_HIGH,
                    fontWeight: 600,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    display: 'inline-block',
                  }}
                >
                  {exposure.content || exposure.exposed_data}
                </Link>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    color: RISK_BORDER_HIGH,
                    fontWeight: 600,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {exposure.exposed_data}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Full comment / post text — show whenever the backend has the full source text */}
        {exposure.full_comment_text && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, letterSpacing: '0.1em', color: TEXT_PRIMARY, lineHeight: 1.2 }}
              >
                {exposure.source === 'post' ? 'Full post text' : 'Full comment'}
              </Typography>
            </Stack>
            <Box
              sx={{
                p: 2,
                bgcolor: 'rgba(22, 35, 58, 0.95)',
                borderRadius: 2,
                border: '1px solid rgba(148,163,184,0.2)',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: TEXT_PRIMARY,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.75,
                  fontSize: '1rem',
                }}
              >
                {exposure.full_comment_text}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Facebook: explain why comment time does not imply a buildable URL (Meta has no public formula) */}
        {exposure.platform === 'facebook' && exposure.source === 'comment' && !resolvedExposureUrl && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2" sx={{ color: TEXT_PRIMARY }}>
              Meta does not allow a post URL to be calculated from the comment date and time alone. A direct link appears
              only when your Download Your Information file includes a post or comment ID, or an embedded facebook.com
              link, for this row.
            </Typography>
          </Alert>
        )}

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25, pt: 0.5 }}>
          <ScheduleIcon sx={{ fontSize: 20, color: TEXT_MUTED }} />
          <Typography
            variant="overline"
            sx={{ fontWeight: 800, letterSpacing: '0.1em', color: TEXT_PRIMARY, lineHeight: 1.2 }}
          >
            Metadata
          </Typography>
        </Stack>
        <Box sx={{ mb: 2 }}>
          {exposure.context && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: TEXT_MUTED, fontWeight: 600 }} gutterBottom>
                Found in context:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: TEXT_PRIMARY }}>
                &quot;...{exposure.context}...&quot;
              </Typography>
            </Box>
          )}

          {/* Link to Post/Comment on platform (secondary action; exposed data is also linked above when URL exists) */}
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {resolvedExposureUrl && (
              <>
                <Link
                  href={resolvedExposureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontWeight: 700, alignSelf: 'flex-start', color: '#40D2B7' }}
                >
                  {exposure.platform === 'youtube' && exposure.source === 'comment' && exposure.url.includes('lc=')
                    ? 'Open this comment on YouTube (edit or delete there) →'
                    : exposure.platform === 'youtube'
                      ? `View on YouTube ${exposure.source === 'post' ? '(channel)' : '(video)'} →`
                      : exposure.platform === 'facebook'
                        ? exposure.source === 'comment'
                          ? 'Open this post on Facebook (edit or delete your comment) →'
                          : 'Open on Facebook →'
                        : `View original ${exposure.source === 'post' ? 'post' : 'comment'} on Reddit →`}
                </Link>
                {exposure.platform === 'youtube' && exposure.source === 'comment' && !exposure.url.includes('lc=') && (
                  <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                    YouTube did not include a direct comment link for this item. On the video page, find your comment,
                    open the menu (⋮), then choose Delete or Edit.
                  </Typography>
                )}
              </>
            )}
            {exposure.platform === 'facebook' && !resolvedExposureUrl && exposure.facebook_activity_fallback_url && (
              <Box>
                <Link
                  href={exposure.facebook_activity_fallback_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontWeight: 700, alignSelf: 'flex-start', color: '#40D2B7' }}
                >
                  Open your Activity log (find this comment) →
                </Link>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, color: TEXT_MUTED }}>
                  Exact post URL was not in your export. Use Activity, or re-upload a fresh Download Your Information ZIP.
                </Typography>
              </Box>
            )}
            {exposure.platform === 'facebook' && !resolvedExposureUrl && !exposure.facebook_activity_fallback_url && (
              <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                No post URL was found in your export for this item. Re-upload your Download Your Information ZIP from the
                dashboard, or find the post via Facebook → Menu → Your activity.
              </Typography>
            )}
            {exposure.created_at &&
              !(
                (exposure.platform === 'facebook' && exposure.source === 'comment' && showFacebookActivity) ||
                (exposure.platform === 'youtube' && exposure.source === 'comment' && showYoutubeActivity)
              ) && (
                <Typography
                  variant="body1"
                  sx={{ color: TEXT_PRIMARY, alignSelf: 'flex-end', fontWeight: 600, fontSize: '1.0625rem' }}
                >
                  {exposure.platform === 'facebook' && exposure.source === 'comment'
                    ? 'Comment time (from your export): '
                    : ''}
                  {formatCommentExportTime(exposure.created_at)}
                </Typography>
              )}
          </Box>

          {exposure.confidence_score && (
            <Typography variant="body2" sx={{ color: TEXT_MUTED, display: 'block', fontSize: '0.9375rem' }}>
              Detection confidence: {(exposure.confidence_score * 100).toFixed(0)}%
            </Typography>
          )}
        </Box>

        {/* Expand/Collapse for Recommendations */}
        <Box sx={{ mt: 2 }}>
          <Button
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'rgba(148, 163, 184, 0.28)',
              color: TEXT_PRIMARY,
              borderRadius: '10px',
              py: 1,
              transition: 'all 0.22s ease',
              '&:hover': {
                borderColor: 'rgba(64, 210, 183, 0.5)',
                bgcolor: 'rgba(64, 210, 183, 0.09)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            {expanded ? 'Hide' : 'Show'} Recommendations
          </Button>
        </Box>

        {/* Recommendations (Collapsible) */}
        <Collapse in={expanded} timeout={280}>
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />

            {exposure.recommendations && exposure.recommendations.length > 0 ? (
              <>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>
                  Recommended Actions:
                </Typography>
                <Stack spacing={1}>
                  {exposure.recommendations.map((rec, index) => (
                    <Alert key={index} severity="info" sx={{ py: 0.5, borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ color: TEXT_PRIMARY }}>
                        {index + 1}. {rec}
                      </Typography>
                    </Alert>
                  ))}
                </Stack>
              </>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>
                  General Recommendations:
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Edit or delete the post/comment containing this information</li>
                  <li>Check your privacy settings on {exposure.platform}</li>
                  <li>Consider using private messaging instead of public posts</li>
                </ul>
              </Alert>
            )}

            {/* Mark as Resolved */}
            {!exposure.is_resolved && onUpdate && (
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '10px',
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.25)',
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 16px rgba(34, 197, 94, 0.32)' },
                  }}
                  onClick={async () => {
                    try {
                      // Check if exposure has an ID
                      if (!exposure._id) {
                        console.error('Exposure missing _id:', exposure);
                        alert('Error: Exposure ID not found. Please refresh the page and try again.');
                        return;
                      }

                      // Get current user to extract username
                      const userData = await authService.getCurrentUser();
                      if (!userData) {
                        alert('Please log in again');
                        return;
                      }

                      const username =
                        userData?.reddit_username ||
                        userData?.username ||
                        userData?.youtube_channel_title ||
                        userData?.facebook_name;
                      if (!username) {
                        alert('Username not found');
                        return;
                      }

                      console.log('Resolving exposure:', {
                        exposure_id: exposure._id,
                        username,
                        platform: exposure.platform || 'reddit',
                      });

                      // Call resolve API
                      const result = await exposureService.resolveExposure(
                        exposure._id,
                        username,
                        exposure.platform || 'reddit',
                        exposure.merged_resolution_ids?.length ? exposure.merged_resolution_ids : null
                      );

                      console.log('Resolve result:', result);

                      // Show success message
                      if (result.status === 'success') {
                        // Show success notification
                        alert('Exposure marked as resolved successfully!');

                        // Small delay to ensure backend has updated
                        setTimeout(() => {
                          // Refresh the exposures list
                          if (onUpdate) {
                            onUpdate();
                          }

                          // Dispatch custom event to notify dashboard to refresh
                          // This works even if dashboard is on a different tab/page
                          window.dispatchEvent(
                            new CustomEvent('exposureResolved', {
                              detail: { exposureId: exposure._id },
                            })
                          );

                          // Also try to refresh if we're on the dashboard page
                          if (window.location.pathname === '/dashboard') {
                            window.location.reload();
                          }
                        }, 500);
                      } else {
                        throw new Error(result.message || 'Failed to resolve exposure');
                      }
                    } catch (error) {
                      console.error('Error resolving exposure:', error);
                      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
                      alert(`Failed to mark exposure as resolved: ${errorMessage}`);
                    }
                  }}
                >
                  Mark as Resolved
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ExposureCard;
